import { Tabs } from '@interstate/components/Tabs';
import InsuranceFormContainer from '../vehicleInsurance/InsuranceFormContainer';
import type { InsuranceFormContainerRef } from '../vehicleInsurance/InsuranceFormContainer';
import { ComplianceForm } from '../compliance/ComplianceForm';
import { useCallback, useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { CustomerRiskAndCoverageTabTypes } from '../types/ComplianceInsuranceTabTypes';
import { CreditBureauForm } from '../creditBureau/CreditBureauForm';
import { useDispatch, useSelector } from 'react-redux';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as complianceActionCreators from '@makemydeal/dr-dash-store/actions/complianceActionCreators';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { VppDealType } from '@makemydeal/dr-platform-types';
import { useValidationGuard } from '../hooks/useValidationGuard';

export interface CustomerRiskAndCoverageTabsRef {
    cancelInsuranceTab: () => boolean;
}

export const CustomerRiskAndCoverageTabs = forwardRef<CustomerRiskAndCoverageTabsRef>((props, ref) => {
    const reduxActiveTab = useSelector(baseDealSelectors.getCustomerRiskAndCoverageSlideoutTab);
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const isShowInsuranceInUnifiedCustomerCardEnabled = useSelector(
        featureToggleSelectors.isShowInsuranceInUnifiedCustomerCardEnabled
    );
    const [activeTab, setActiveTab] = useState<string>(
        isShowInsuranceInUnifiedCustomerCardEnabled ? reduxActiveTab : CustomerRiskAndCoverageTabTypes.INSURANCE
    );
    const isCreditProviderRouteOne: boolean = useSelector(dealerSelectors.isCreditProviderRouteOne);
    const { dealType } = useSelector(vehicleProtectionSelectors.getVppSurchargesOverrides);
    const guard = useValidationGuard('exit', { skipValidationKeys: ['vehicle'] });
    const insuranceFormRef = useRef<InsuranceFormContainerRef>(null);

    const dispatch = useDispatch();

    useEffect(() => {
        setActiveTab(reduxActiveTab);
    }, [reduxActiveTab]);

    useImperativeHandle(ref, () => ({
        cancelInsuranceTab: () => {
            // Only call cancel if we're on the insurance tab
            if (
                !isShowInsuranceInUnifiedCustomerCardEnabled &&
                activeTab === CustomerRiskAndCoverageTabTypes.INSURANCE &&
                insuranceFormRef.current
            ) {
                insuranceFormRef.current.cancel();
                return true; // Handled by insurance form
            }
            return false; // Not on insurance tab, didn't handle it
        }
    }));

    const handleActivate = useCallback(
        (val: CustomerRiskAndCoverageTabTypes) => {
            if (val === CustomerRiskAndCoverageTabTypes.CREDIT_BUREAU) {
                guard.wrap(() => {
                    dispatch(complianceActionCreators.clearResendIdVerification());
                    dispatch(baseDealActionCreators.setCustomerRiskAndCoverageTab(CustomerRiskAndCoverageTabTypes.CREDIT_BUREAU));
                })();
            } else {
                dispatch(complianceActionCreators.clearResendIdVerification());
                dispatch(baseDealActionCreators.setCustomerRiskAndCoverageTab(val));
            }
        },
        [guard, dispatch]
    );

    const getTabs = (activeTab: string) => {
        const tabs = [];

        if (!isShowInsuranceInUnifiedCustomerCardEnabled) {
            tabs.push({
                component: (
                    <div data-testid="insurance-form">
                        <InsuranceFormContainer ref={insuranceFormRef} />
                    </div>
                ),
                value: CustomerRiskAndCoverageTabTypes.INSURANCE,
                label: 'Insurance'
            });
        }

        // Add Compliance tab only for Ecommerce deals, but not for non-Ecommerce Cash deals
        const isNonEcommerceCashDeal = !isEcommerceDeal && dealType === VppDealType.CASH;

        if (isEcommerceDeal && !isNonEcommerceCashDeal) {
            tabs.push({
                component: (
                    <div data-testid="compliance-form" style={{ marginBottom: '1rem' }}>
                        <ComplianceForm />
                    </div>
                ),
                value: CustomerRiskAndCoverageTabTypes.COMPLIANCE,
                label: 'Compliance'
            });
        }

        // Only add Credit Bureau tab if:
        // 1. isCreditProviderRouteOne is false
        if (!isCreditProviderRouteOne) {
            tabs.push({
                value: CustomerRiskAndCoverageTabTypes.CREDIT_BUREAU,
                label: 'Credit Bureau',
                component:
                    reduxActiveTab === CustomerRiskAndCoverageTabTypes.CREDIT_BUREAU ? (
                        <CreditBureauForm />
                    ) : (
                        <div data-testid="credit-bureau-form">Credit Bureau</div>
                    )
            });
        }

        return tabs.map((item) => ({
            ...item,
            active: activeTab === item.value
        }));
    };

    return (
        <Tabs
            data-testid="compliance-insurance-tabs"
            id="compliance-insurance-tabs"
            onActivate={handleActivate}
            tabs={getTabs(activeTab)}
        />
    );
});

CustomerRiskAndCoverageTabs.displayName = 'CustomerRiskAndCoverageTabs';
